import React from 'react';
import { X, ExternalLink } from 'lucide-react';

function CustomizationDetailModal({
  customization,
  pattern,
  colors,
  fonts,
  onClose,
  onContinueEditing,
  onDelete,
  user,
  supabase
}) {
  if (!customization) return null;

  const safeParse = (val, fallback) => {
    if (!val) return fallback;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return fallback; }
  };
  const yarnColors = safeParse(customization.yarn_colors, {});
  const notes = safeParse(customization.notes, {});

  const displayTitle = pattern?.title || notes.title || 'Custom Design';
  const displayImage = pattern?.image || notes.imageUrl || null;
  const displaySourceUrl = pattern?.sourceUrl || notes.sourceUrl || null;

  const originalSpecs = notes.originalSpecs || {};
  const yarnSlots = notes.yarnSlots || [];
  const totalYards = yarnSlots.reduce((sum, slot) => sum + (slot.yards || 0), 0);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '2rem',
        overflow: 'auto'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '20px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          fontFamily: fonts.body
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'sticky',
            top: '1.5rem',
            left: 'calc(100% - 60px)',
            background: 'rgba(255,255,255,0.9)',
            border: 'none',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '1.5rem',
            color: colors.brownGray,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 10,
            marginBottom: '-56px',
            marginRight: '1rem'
          }}
        >
          <X size={24} />
        </button>

        {/* Hero Image */}
        <div
          style={{
            position: 'relative',
            height: '300px',
            background: `linear-gradient(135deg, ${colors.mustard}, #c89b68)`,
            borderRadius: '20px 20px 0 0',
            overflow: 'hidden'
          }}
        >
          {displayImage && (
            <img
              src={displayImage}
              alt={displayTitle}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          )}
          <div
            style={{
              position: 'absolute',
              bottom: '1.5rem',
              left: '2rem',
              background: 'rgba(255,255,255,0.95)',
              padding: '0.75rem 1.5rem',
              borderRadius: '50px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: '700',
              fontSize: '0.9rem',
              color: colors.mustard
            }}
          >
            🎨 Custom Design
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          {/* Pattern Title */}
          <h2
            style={{
              fontSize: '2rem',
              fontFamily: fonts.display,
              color: colors.charcoal,
              marginBottom: '0.5rem'
            }}
          >
            {displayTitle}
          </h2>
          <p style={{ color: colors.darkGray, marginBottom: '2rem' }}>
            Your Custom Version
          </p>


          {/* Color Palette */}
          {Object.keys(yarnColors).length > 0 && (
            <div
              style={{
                background: '#fff',
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem'
              }}
            >
              <h3
                style={{
                  fontSize: '1.3rem',
                  color: colors.charcoal,
                  marginBottom: '1rem',
                  fontWeight: '700'
                }}
              >
                🎨 Your Color Palette
              </h3>
              <div
                style={{
                  display: 'grid',
                  gap: '1rem'
                }}
              >
                {Object.entries(yarnColors).map(([yarnName, colorInfo]) => {
                  const slot = yarnSlots.find(s => s.name === yarnName);
                  return (
                    <div
                      key={yarnName}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        padding: '1rem',
                        background: colors.bgCream,
                        borderRadius: '8px'
                      }}
                    >
                      {/* Color Swatch */}
                      <div
                        style={{
                          width: '60px',
                          height: '60px',
                          background: colorInfo.isCustom ? colorInfo.hex : colors.borderGreige,
                          borderRadius: '8px',
                          border: `2px solid ${colors.borderGreige}`,
                          flexShrink: 0,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          overflow: 'hidden',
                        }}
                      >
                        {!colorInfo.isCustom && colorInfo.imageUrl && (
                          <img src={colorInfo.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                      </div>
                      {/* Color Info */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.charcoal, marginBottom: '0.25rem' }}>
                          {colorInfo.name}{colorInfo.family ? ` (${colorInfo.family})` : ''}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: colors.brownGray }}>
                          Pattern Yarn: {yarnName}
                        </div>
                        {slot?.yards && (
                          <div style={{ fontSize: '0.8rem', color: colors.brownGray, marginTop: '0.2rem' }}>
                            ~{slot.yards} yds needed
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total Yarn */}
              {totalYards > 0 && (
                <div
                  style={{
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    borderTop: `2px solid ${colors.borderGreige}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ fontSize: '1.2rem', fontWeight: '700', color: colors.charcoal }}>
                    Total Yarn Needed:
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.mustard }}>
                    {totalYards} yards
                  </div>
                </div>
              )}

              {/* Pro Tip */}
              <div
                style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: '#fffbf0',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  color: colors.darkGray
                }}
              >
                <strong>💡 Pro Tip:</strong> Yardage estimates are based on the original pattern yarn. If substituting a different weight, you may need more or less yarn. Always buy an extra 10-20% to be safe.
                {totalYards > 0 && <> Recommended: {Math.round(totalYards * 1.15)} yards total.</>}
              </div>
            </div>
          )}

          {/* Specs Summary */}
          <div
            style={{
              background: colors.bgCream,
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '2rem'
            }}
          >
            <h3
              style={{
                fontSize: '1.3rem',
                color: colors.charcoal,
                marginBottom: '1rem',
                fontWeight: '700'
              }}
            >
              Pattern Specs
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem' }}>
              {pattern?.originalSize?.width && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: colors.brownGray, textTransform: 'uppercase', marginBottom: '0.35rem' }}>Size</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.charcoal }}>{pattern.originalSize.width}" × {pattern.originalSize.height}"</div>
                </div>
              )}
              {pattern?.difficulty && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: colors.brownGray, textTransform: 'uppercase', marginBottom: '0.35rem' }}>Difficulty</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.charcoal }}>{pattern.difficulty}</div>
                </div>
              )}
              {(originalSpecs.hook || pattern?.hook)?.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: colors.brownGray, textTransform: 'uppercase', marginBottom: '0.35rem' }}>Hook Size</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.charcoal }}>
                    {(() => { const h = originalSpecs.hook || pattern?.hook; return Array.isArray(h) ? h.join(', ') : h; })()}
                  </div>
                </div>
              )}
              {pattern?.yarnWeight && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: colors.brownGray, textTransform: 'uppercase', marginBottom: '0.35rem' }}>Yarn Weight</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.charcoal }}>{pattern.yarnWeight}</div>
                </div>
              )}
              {pattern?.yarnMaterial && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: colors.brownGray, textTransform: 'uppercase', marginBottom: '0.35rem' }}>Yarn Material</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.charcoal }}>{pattern.yarnMaterial}</div>
                </div>
              )}
              {pattern?.constructionMethod && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: colors.brownGray, textTransform: 'uppercase', marginBottom: '0.35rem' }}>Construction</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.charcoal }}>{pattern.constructionMethod}</div>
                </div>
              )}
              {pattern?.gaugeEntries?.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: colors.brownGray, textTransform: 'uppercase', marginBottom: '0.35rem' }}>Gauge</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.charcoal }}>
                    {pattern.gaugeEntries.map((entry, idx) => {
                      const unitLabel = entry.unit === '2in' ? '2"' : '4"';
                      let text = '';
                      if (entry.type === 'standard') text = `${entry.stitches} st${entry.rows ? ` × ${entry.rows} rows` : ''} = ${unitLabel}`;
                      else if (entry.type === 'granny') text = `Finished square = ${entry.square_inches}"`;
                      else if (entry.type === 'c2c') text = `${entry.blocks} blocks = ${unitLabel}`;
                      else if (entry.type === 'note') text = entry.note || '';
                      const label = entry.label ? ` (${entry.label})` : '';
                      return <div key={idx} style={idx > 0 ? { marginTop: '0.25rem' } : {}}>{text}{label}</div>;
                    })}
                  </div>
                </div>
              )}
              {pattern?.totalYards > 0 && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: colors.brownGray, textTransform: 'uppercase', marginBottom: '0.35rem' }}>Total Yarn</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.charcoal }}>{pattern.totalYards} yards</div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              paddingTop: '1rem',
              borderTop: `2px solid ${colors.borderGreige}`
            }}
          >
            {/* Start Project */}
            <button
              onClick={async () => {
                if (!user) {
                  alert('Please sign in to start a project.');
                  return;
                }

                // Create a saved_pattern entry for this design
                const { error } = await supabase
                  .from('saved_patterns')
                  .insert({
                    user_id: user.id,
                    pattern_id: pattern?.id || null,
                    status: 'in_progress',
                    started_at: new Date().toISOString(),
                    notes: Object.entries(yarnColors).map(([name, color]) => {
                      const slot = yarnSlots.find(s => s.name === name);
                      return `${name}: ${color.name}${slot?.yards ? ` (~${slot.yards} yds)` : ''}`;
                    }).join(', ')
                  });

                if (error) {
                  console.error('Error starting project:', error);
                  alert('Failed to start project. Please try again.');
                } else {
                  alert('Project started! See it in the Project Dashboard > In Progress tab.');
                  onClose();
                  window.location.reload();
                }
              }}
              style={{
                padding: '1rem',
                background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
                border: 'none',
                borderRadius: '12px',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 15px rgba(163, 177, 138, 0.3)'
              }}
            >
              Start Project
            </button>

            {/* View Original Pattern */}
            {displaySourceUrl && (
              <a
                href={displaySourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '1rem',
                  background: '#fff',
                  border: `2px solid ${colors.dustyRust}`,
                  borderRadius: '12px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: colors.dustyRust,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  textDecoration: 'none'
                }}
              >
                View Original
                <ExternalLink size={16} />
              </a>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(customization)}
                style={{
                  padding: '0.75rem',
                  background: 'transparent',
                  border: `1px solid ${colors.borderGreige}`,
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  color: colors.brownGray,
                  cursor: 'pointer',
                  marginTop: '0.5rem'
                }}
              >
                Delete Design
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomizationDetailModal;
